
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Video, Company } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import VideoCard from '../../components/common/VideoCard';
import Spinner from '../../components/common/Spinner';
import { AuthContext } from '../../contexts/AuthContext';
import ConfirmationModal from '../../components/common/ConfirmationModal';

const UploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
);

const CompanyVideosPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [videos, setVideos] = useState<Video[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const authContext = useContext(AuthContext);
  const userRole = authContext?.user?.role;
  const tenantId = authContext?.user?.tenantId;

  const fetchData = useCallback(async () => {
    if (companyId && tenantId) {
      setLoading(true);
      try {
          // Fixed: Added tenantId to getVideos call
          const [fetchedVideos, fetchedCompany] = await Promise.all([
              firestoreService.getVideos(tenantId, companyId),
              firestoreService.getCompany(companyId)
          ]);
          setVideos(fetchedVideos);
          setCompany(fetchedCompany || null);
      } catch (error) {
          console.error("Failed to fetch company videos:", error);
      } finally {
          setLoading(false);
      }
    }
  }, [companyId, tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteClick = (video: Video) => {
    setVideoToDelete(video);
  };

  const confirmDeleteVideo = async () => {
    if (!videoToDelete) return;
    setIsDeleting(true);
    try {
        await firestoreService.deleteVideo(videoToDelete.videoId);
        await fetchData(); // Refresh the list
        setVideoToDelete(null);
    } catch (error) {
        console.error("Failed to delete video:", error);
        alert(`There was an error deleting the video: ${(error as Error).message}`);
    } finally {
        setIsDeleting(false);
    }
  };

  const filteredVideos = videos.filter(video =>
    video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    video.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <Link to="/videos" className="text-blue-400 hover:text-blue-300 mb-6 inline-flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Companies
      </Link>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">Videos for {company?.name}</h1>
        <Link 
            to="/videos/new"
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2"
        >
            <UploadIcon />
            Upload Video
        </Link>
      </div>

      <div className="mb-6">
        <input
          type="text"
          placeholder="Search videos in this company..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full max-w-lg px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredVideos.length > 0 ? filteredVideos.map(video => (
          <VideoCard 
            key={video.videoId} 
            video={video}
            userRole={userRole}
            onDelete={handleDeleteClick} 
          />
        )) : <p className="text-gray-400 col-span-full">No videos found for this company.</p>}
      </div>
      
      <ConfirmationModal
        isOpen={!!videoToDelete}
        onClose={() => setVideoToDelete(null)}
        onConfirm={confirmDeleteVideo}
        title="Delete Video"
        message={<>Are you sure you want to delete the video <strong>{videoToDelete?.title}</strong>? This will also delete its AI analysis. This action cannot be undone.</>}
        isConfirming={isDeleting}
      />
    </div>
  );
};

export default CompanyVideosPage;
